import React from 'react'
import { useQuery } from 'react-query'
import { useSearchParams, Link } from 'react-router-dom'

import Button from 'components/Button'
import { postReportVoucherRedemptionCountService } from 'api/reports-voucher-redemption-count'
import { ExportToCSV } from '../../../reports-list/utils-reports-actions'

import { TRANSACTION_REPORT_CALENDAR_UNIT } from 'constants/filters'

type VoucherIssue = { voucherId: number; voucherName: string; issueCount: number }
type VoucherRedemptionItem = { unit: string; voucherId: number; redemptionCount: number }
type SearchQuery = { startDate: string; endDate: string; groupLevel: string }

const groupLevelToCalendarUnit = (groupLevel: number | string) => {
  switch (Number(groupLevel)) {
    case 1:
      return TRANSACTION_REPORT_CALENDAR_UNIT[0]
    case 2:
      return TRANSACTION_REPORT_CALENDAR_UNIT[1]
    case 3:
      return TRANSACTION_REPORT_CALENDAR_UNIT[2]
    case 4:
      return TRANSACTION_REPORT_CALENDAR_UNIT[3]
    default:
      return TRANSACTION_REPORT_CALENDAR_UNIT[0]
  }
}

const PieChart = ({
  data,
  size = 160,
  centerText,
}: {
  data: { label: string; value: number; color?: string }[]
  size?: number
  centerText?: string
}) => {
  const total = data.reduce((s, d) => s + d.value, 0)
  if (total === 0) return null
  let cumulative = 0
  const radius = Math.max(40, Math.floor(size / 2) - 8)
  const cx = Math.floor(size / 2)
  const cy = Math.floor(size / 2)

  const sectorPath = (start: number, end: number) => {
    const startRad = (start / total) * Math.PI * 2 - Math.PI / 2
    const endRad = (end / total) * Math.PI * 2 - Math.PI / 2
    const x1 = cx + radius * Math.cos(startRad)
    const y1 = cy + radius * Math.sin(startRad)
    const x2 = cx + radius * Math.cos(endRad)
    const y2 = cy + radius * Math.sin(endRad)
    const large = end - start > total / 2 ? 1 : 0
    return `M ${cx} ${cy} L ${x1} ${y1} A ${radius} ${radius} 0 ${large} 1 ${x2} ${y2} Z`
  }

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      {data.length === 1 ? (
        (() => {
          const d = data[0]
          const color = d.color || `hsl(200 70% 50%)`
          return <circle cx={cx} cy={cy} r={radius} fill={color} stroke="#fff" />
        })()
      ) : (
        data.map((d) => {
          const start = cumulative
          cumulative += d.value
          const end = cumulative
          const path = sectorPath(start, end)
          const color = d.color || `hsl(200 70% 50%)`
          return <path key={d.label} d={path} fill={color} stroke="#fff" />
        })
      )}
      {centerText && (
        <text x={cx} y={cy} textAnchor="middle" dominantBaseline="central" fontSize={12} fill="#111">
          {centerText}
        </text>
      )}
    </svg>
  )
}

export const ViewVoucherRedemption = () => {
  const [searchParams] = useSearchParams()
  const searchQuery = JSON.parse(decodeURIComponent(searchParams.get('report-query')!))
  console.log('Search Query:', searchQuery
  )
  const searchRows: SearchQuery[] = Array.isArray(searchQuery) ? searchQuery : [searchQuery]
  const { data, isLoading, isError } = useQuery(['voucherRedemptionReport', searchQuery], () => postReportVoucherRedemptionCountService(searchQuery))

  if (isLoading) return <div>Loading...</div>
  if (isError) return <div>Error loading report</div>

  const apiData = data?.data

  const issueCounts = apiData?.voucherIssueCounts || []
  const redemptionCounts = apiData?.voucherRedemptionCounts || []

  const unit = redemptionCounts.length > 0 ? redemptionCounts[0].unit : 'N/A'
  const voucherName = issueCounts.length > 0 ? issueCounts[0].voucherName : 'N/A'
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const voucherNameById: Record<number, string> = issueCounts.reduce((acc, it: any) => {
    acc[it.voucherId] = it.voucherName || acc[it.voucherId] || 'N/A'
    return acc
  }, {} as Record<number, string>)

  const redemptionByVoucher = redemptionCounts.reduce<Record<number, number>>((acc, item: VoucherRedemptionItem) => {
    acc[item.voucherId] = (acc[item.voucherId] || 0) + item.redemptionCount
    return acc
  }, {})

  const issueByVoucher = issueCounts.reduce<Record<number, number>>((acc, item: VoucherIssue) => {
    acc[item.voucherId] = (acc[item.voucherId] || 0) + item.issueCount
    return acc
  }, {})

  const combinedVoucherIds = Array.from(new Set([...Object.keys(issueByVoucher).map(Number), ...Object.keys(redemptionByVoucher).map(Number)]))
  const sortedVoucherIds = combinedVoucherIds.sort((a, b) => a - b)
  const csvRows = sortedVoucherIds.map((vid) => ({
    voucherId: vid,
    voucherName: voucherNameById[vid] || voucherName,
    issued: issueByVoucher[vid] || 0,
    redeemed: redemptionByVoucher[vid] || 0,
  }))

  return (
    <div className="my-6">
      <div className="flex items-center gap-4">
        <button className="px-3 py-1 border rounded-md border-primary text-primary" type="button">
          <Link to={`/reports-list?modal=voucher_redemption`} className="flex items-center gap-2">
            <span className="tracking-wider font-light">Back to reports</span>
          </Link>
        </button>

        <Button
          type="button"
          className="ml-1 rounded-lg tracking-wide font-bold"
          iconRight={false}
          onClick={() => ExportToCSV(csvRows, { filename: 'Voucher-Issues-vs-Redemptions' })}
        >
          Download CSV Report
        </Button>
      </div>

      <br />

      <div className="flex gap-10 items-start">
        <div>
          <h4 className="mb-1 font-bold">Criteria Used</h4>
          <table className="table-auto border-collapse">
            <thead>
              <tr>
                <th className="px-2 pl-0">Start Date</th>
                <th className="px-2">End Date</th>
                <th className="px-2">Group By</th>
              </tr>
            </thead>
            <tbody>
              {searchRows.map((row: SearchQuery) => (
                <tr key={row.startDate + row.endDate}>
                  <td className="px-2 pl-0">{row.startDate.slice(0, 10)}</td>
                  <td className="px-2">{row.endDate.slice(0, 10)}</td>
                  <td className="px-2">{groupLevelToCalendarUnit(Number(row.groupLevel)).label}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex gap-10 items-start mt-8">
        <div>
          <h4 className="mb-1 font-bold">Redemptions by Voucher</h4>
          {sortedVoucherIds.length ? (
            <table className="table-auto border-collapse">
              <thead>
                <tr>
                  <th className="px-2 pl-0 text-left">Voucher</th>
                  <th className="px-2 text-left">Unit</th>
                  <th className="px-2 text-left">Redeemed</th>
                </tr>
              </thead>
              <tbody>
                {sortedVoucherIds.map((id) => (
                  <tr key={id}>
                    <td className="px-2 pl-0 text-left">{id} - {voucherNameById[id] || voucherName}</td>
                    <td className="px-2 text-left">{unit || 'N/A'}</td>
                    <td className="px-2 text-left">{redemptionByVoucher[id] || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p>No redemption data available</p>
          )}
        </div>

        <div>
          <h4 className="mb-1 font-bold">Issue Counts</h4>
          <table className="table-auto border-collapse">
            <thead>
              <tr>
                <th className="px-2 pl-0 text-left">Voucher</th>
                <th className="px-2 text-left">Issued</th>
              </tr>
            </thead>
            <tbody>
              {sortedVoucherIds.map((id) => (
                <tr key={id}>
                  <td className="px-2 pl-0 text-left">{id} - {voucherNameById[id] || voucherName}</td>
                  <td className="px-2 text-left">{issueByVoucher[id] || 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-8">
        <h4 className="font-bold">Per Voucher Charts</h4>
        {combinedVoucherIds.length === 0 ? (
          <p className="mt-2">No voucher data available</p>
        ) : (
          <div className="flex gap-6 flex-wrap">
            {combinedVoucherIds.slice(0, 5).map((vid) => {
              const issued = issueByVoucher[vid] || 0
              const redeemed = redemptionByVoucher[vid] || 0
              const chartData = [] as { label: string; value: number; color?: string }[]
              // issued = red, redeemed = blue
              if (issued > 0) chartData.push({ label: 'Issued', value: issued, color: '#e53e3e' })
              if (redeemed > 0) chartData.push({ label: 'Redeemed', value: redeemed, color: '#3182ce' })

              return (
                <div key={vid} className="w-44 bg-white p-2 rounded shadow-sm">
                  <div className="flex justify-center">
                    {chartData.length ? (
                      <PieChart data={chartData} size={140} />
                    ) : (
                      <div className="text-center text-sm text-gray-600">No data</div>
                    )}
                  </div>
                  <div className="mt-2 text-center text-sm font-bold">{vid}</div>
                  {/* <div className="text-center text-sm">{voucherName}</div> */}
                  <div className="mt-3 flex justify-center gap-4 text-sm">
                    <div className="flex items-center gap-1">
                      <span style={{ width: 12, height: 12, background: '#e53e3e', display: 'inline-block' }} />
                      <span>Issued</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <span style={{ width: 12, height: 12, background: '#3182ce', display: 'inline-block' }} />
                      <span>Redeemed</span>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

export default ViewVoucherRedemption
